import { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import {
	type ContentExtensionMetrics,
	retrieveContentExtensionMetricsFromNode,
} from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers.ts';

import { traverseWithNestedContentAndDepth } from './traverseWithNestedContent.ts';

/** The nodes whose body an expand hides: `expand` and `nestedExpand` render collapsed. */
const EXPAND_NODE_TYPES = new Set(['expand', 'nestedExpand']);

export type AdfStats = {
	contentExtensionMetrics: ContentExtensionMetrics;
	nodeCountByType: Record<string, number>;
	totalNodeCount: number;
	/**
	 * How many of `totalNodeCount` the reader cannot see until they open something: the body of
	 * every expand, however deeply nested. The expand itself is not one of them — its title shows.
	 *
	 * Counted whatever the renderer does with those nodes. A collapsed body is out of view either
	 * way; whether it is also left unrendered is a separate question, for whoever reads this.
	 */
	totalNodesHiddenFromView: number;
};

type CustomPerfData =
	| {
			contentExtensionMetrics?: ContentExtensionMetrics;
	  }
	| null
	| undefined;

export function useAdfStats(adfDocument: DocNode, customPerfData?: CustomPerfData): AdfStats {
	const shouldRetrieveContentExtensionMetrics = Boolean(customPerfData);

	const adfStats = useMemo(() => {
		const contentExtensionMetrics: ContentExtensionMetrics = {
			contentExtensionCount: {
				connect: {},
				forge: {},
			},
			contentExtensionKeys: {
				connect: [],
				forge: [],
			},
			depth: {
				connect: [],
				forge: [],
			},
			hasExtensions: false,
		};
		const nodeCountByType: Record<string, number> = {};
		let totalNodeCount = 0;
		let totalNodesHiddenFromView = 0;

		// The depth of the outermost expand currently open, or `undefined` outside one. The traversal
		// is depth-first in document order, so everything deeper than this is inside that expand and
		// the first node at this depth or shallower is past it. A nested expand needs no entry of its
		// own: it is already inside this one.
		let openExpandDepth: number | undefined;

		const recordNode = (node: ADFEntity, depth?: number) => {
			if (openExpandDepth !== undefined && depth !== undefined && depth <= openExpandDepth) {
				openExpandDepth = undefined;
			}

			const isHiddenFromView = openExpandDepth !== undefined;

			nodeCountByType[node.type] = (nodeCountByType[node.type] || 0) + 1;
			totalNodeCount++;
			if (isHiddenFromView) {
				totalNodesHiddenFromView++;
			} else if (EXPAND_NODE_TYPES.has(node.type) && depth !== undefined) {
				openExpandDepth = depth;
			}

			if (shouldRetrieveContentExtensionMetrics) {
				retrieveContentExtensionMetricsFromNode(node, contentExtensionMetrics, depth);
			}
		};

		traverseWithNestedContentAndDepth(adfDocument, recordNode);

		return {
			contentExtensionMetrics,
			nodeCountByType,
			totalNodeCount,
			totalNodesHiddenFromView,
		};
	}, [adfDocument, shouldRetrieveContentExtensionMetrics]);

	if (shouldRetrieveContentExtensionMetrics) {
		customPerfData!.contentExtensionMetrics = adfStats.contentExtensionMetrics;
	}

	return adfStats;
}
